/**
 * Groups feature API layer — OpenAPI-first (§9.1, §9.3).
 *
 * Admin groups CRUD + import oracle + public groups (read-only).
 */

import {
  getAdminGroupsListQueryKey,
  useAdminGroupsGet as useGenAdminGet,
  useAdminGroupsList as useGenAdminList,
  useAdminGroupsCreate as useGenCreate,
  useAdminGroupsDelete as useGenDelete,
  useAdminGroupsImportOracle as useGenImportOracle,
  useAdminGroupsUpdate as useGenUpdate,
} from "@shared/api/generated/admin/admin";
import {
  useGroupsGet as useGenGroupsGet,
  useGroupsList as useGenGroupsList,
} from "@shared/api/generated/groups/groups";
import type { AdminGroupsListParams, GroupsListParams } from "@shared/api/generated/model";
import { useQueryClient } from "@tanstack/react-query";

export type {
  DirectoryGroup,
  DirectoryGroupChannel,
  DirectoryGroupMember,
  ImportOracleGroupsResponse,
} from "@shared/api/generated/model";

// ── Filter interface (matches old hooks.ts API) ─────────────────────────────

export interface DirectoryGroupFilters {
  name?: string;
  object?: { regionId: number; objectId: number };
  level?: number;
}

// ── Admin queries ───────────────────────────────────────────────────────────

export const useAdminGroups = (filters?: DirectoryGroupFilters) => {
  const params: AdminGroupsListParams = {
    ...(filters?.name && { name: filters.name }),
    ...(filters?.object && {
      region: filters.object.regionId,
      object: filters.object.objectId,
    }),
    ...(filters?.level && { level: filters.level }),
  };
  return useGenAdminList(params, { query: { staleTime: 60_000 } });
};

export const useAdminGroup = (id?: string) => {
  const enabled = Boolean(id && id !== "new");
  return useGenAdminGet(id ?? "", { query: { enabled, staleTime: 60_000 } });
};

// ── Public queries (read-only, all authenticated users) ─────────────────────

export const useGroups = (filters?: DirectoryGroupFilters) => {
  const params: GroupsListParams = {
    ...(filters?.name && { name: filters.name }),
    ...(filters?.object && {
      region: filters.object.regionId,
      object: filters.object.objectId,
    }),
    ...(filters?.level && { level: filters.level }),
  };
  return useGenGroupsList(params, { query: { staleTime: 60_000 } });
};

export const useGroup = (id?: string) => {
  const enabled = Boolean(id && id !== "new");
  return useGenGroupsGet(id ?? "", { query: { enabled, staleTime: 60_000 } });
};

// ── Mutations ───────────────────────────────────────────────────────────────

export const useCreateAdminGroup = () => {
  const qc = useQueryClient();
  return useGenCreate({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getAdminGroupsListQueryKey() });
      },
    },
  });
};

export const useUpdateAdminGroup = () => {
  const qc = useQueryClient();
  return useGenUpdate({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getAdminGroupsListQueryKey() });
      },
    },
  });
};

export const useDeleteAdminGroup = () => {
  const qc = useQueryClient();
  return useGenDelete({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getAdminGroupsListQueryKey() });
      },
    },
  });
};

export const useImportOracleGroups = () => {
  const qc = useQueryClient();
  return useGenImportOracle({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getAdminGroupsListQueryKey() });
      },
    },
  });
};
